import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import "./OrganisationOverview.scss";
import { IndianRupee, Zap, CalendarClock, AlertCircle, Search } from "lucide-react";
import {
  getOrgSettlementSummary,
  getOrgSettlementStatements,
  getOrgSettlementCovering,
  OrgSettlementSummary,
  OrgStatementRow,
} from "../../api/settlement";
import { useUser } from "../../context/UserContext";

// --- formatting helpers ------------------------------------------------------
const inr = (n: number): string =>
  `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const kwh = (n: number): string =>
  `${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })} kWh`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtPeriodKey = (key: string): string => {
  if (key.includes("-Q")) { const [y, q] = key.split("-Q"); return `Q${q} ${y}`; }
  const [y, m] = key.split("-"); const mi = Number(m) - 1;
  return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${y}` : key;
};

// --- inline styles (white cards, subtle borders, #333 text) ------------------
const styles: { [k: string]: React.CSSProperties } = {
  page: { color: "#333" },
  h1: { fontSize: "1.5rem", fontWeight: 700, margin: "0 0 0.25rem", color: "#1f2937" },
  sub: { fontSize: "0.85rem", color: "#6b7280", margin: "0 0 1.25rem" },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
    marginBottom: "1.5rem",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "1.1rem 1.25rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  kpiLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "0.78rem",
    color: "#6b7280",
    marginBottom: "0.4rem",
  },
  kpiValue: { fontSize: "1.5rem", fontWeight: 700, color: "#111827" },
  sectionCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "1.25rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    marginBottom: "1.5rem",
  },
  sectionTitle: { fontSize: "1.05rem", fontWeight: 600, margin: "0 0 0.25rem", color: "#1f2937" },
  whereItGoes: {
    fontSize: "0.85rem",
    color: "#0f766e",
    background: "rgba(45,212,191,0.08)",
    border: "1px solid rgba(45,212,191,0.35)",
    borderLeft: "3px solid #2dd4bf",
    borderRadius: "0 8px 8px 0",
    padding: "0.75rem 1rem",
    margin: "0.75rem 0 1.25rem",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", minWidth: "640px" },
  th: {
    textAlign: "left",
    padding: "0.6rem 0.75rem",
    borderBottom: "1px solid #e5e7eb",
    color: "#6b7280",
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },
  thR: { textAlign: "right" },
  td: {
    padding: "0.7rem 0.75rem",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
    whiteSpace: "nowrap",
  },
  tdR: { textAlign: "right" },
  tdStrong: { fontWeight: 700, color: "#111827" },
  pill: {
    display: "inline-block",
    padding: "0.2rem 0.6rem",
    borderRadius: "999px",
    fontSize: "0.72rem",
    fontWeight: 700,
  },
  pillPaid: { background: "#dcfce7", color: "#166534" },
  pillAccruing: { background: "#fef9c3", color: "#92400e" },
  dateInput: { border: "1px solid #d1d5db", borderRadius: "8px", padding: "0.45rem 0.6rem", fontSize: "0.85rem" },
  btn: { background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", padding: "0.5rem 0.9rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.4rem" },
  state: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "2.5rem 1.5rem",
    textAlign: "center",
    color: "#6b7280",
  },
  errorBox: { display: "flex", alignItems: "center", gap: "0.6rem", justifyContent: "center", color: "#991b1b" },
};

// --- layout shell (mirrors OrganisationOverview) -----------------------------
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);
  return (
    <div className="organisation-overview">
      <Sidebar className={isSidebarOpen ? "open" : ""} />
      {isSidebarOpen && <div className="overlay" onClick={closeSidebar} />}
      <div className="main-content-wrapper">
        <Header onMenuClick={toggleSidebar} />
        <main className="overview-content" style={styles.page}>
          {children}
        </main>
      </div>
    </div>
  );
};

const StatementsTable: React.FC<{ rows: OrgStatementRow[]; empty: string }> = ({ rows, empty }) => (
  <div style={styles.tableWrap}>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Period</th>
          <th style={styles.th}>Range</th>
          <th style={{ ...styles.th, ...styles.thR }}>Energy</th>
          <th style={{ ...styles.th, ...styles.thR }}>My total</th>
          <th style={{ ...styles.th, ...styles.thR }}>GST</th>
          <th style={styles.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? (
          rows.map((row) => (
            <tr key={row.period_key}>
              <td style={{ ...styles.td, ...styles.tdStrong }}>{fmtPeriodKey(row.period_key)}</td>
              <td style={styles.td}>{row.period_start}–{row.period_end}</td>
              <td style={{ ...styles.td, ...styles.tdR }}>{kwh(row.kwh)}</td>
              <td style={{ ...styles.td, ...styles.tdR }}>{inr(row.total)}</td>
              <td style={{ ...styles.td, ...styles.tdR }}>{inr(row.gst)}</td>
              <td style={styles.td}>
                {row.paid ? (
                  <span style={{ ...styles.pill, ...styles.pillPaid }}>Paid</span>
                ) : (
                  <span style={{ ...styles.pill, ...styles.pillAccruing }}>Awaiting payout</span>
                )}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td style={{ ...styles.td, color: "#9ca3af" }} colSpan={6}>{empty}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const OrgSettlements: React.FC = () => {
  // The app resolves the tenant from the auth context (same as Dashboard etc.).
  const { user } = useUser();
  const tenantId = user?.org_id != null ? String(user.org_id) : null;

  const [summary, setSummary] = useState<OrgSettlementSummary | null>(null);
  const [statements, setStatements] = useState<OrgStatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // covering-quarters span lookup
  const [spanFrom, setSpanFrom] = useState("");
  const [spanTo, setSpanTo] = useState("");
  const [covering, setCovering] = useState<OrgStatementRow[] | null>(null);
  const [spanBusy, setSpanBusy] = useState(false);
  const [spanError, setSpanError] = useState("");

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const stmts = await getOrgSettlementStatements(tenantId);
        if (cancelled) return;
        setStatements(stmts);
        // default the summary to the latest SETTLED quarter (statements are settled-only)
        const sum = await getOrgSettlementSummary(tenantId, stmts[0]?.period_key);
        if (cancelled) return;
        setSummary(sum);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load settlements:", err);
        setError("We couldn't load your settlements right now. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const runSpan = async () => {
    if (!tenantId) return;
    if (!spanFrom || !spanTo) {
      setSpanError("Pick both dates.");
      return;
    }
    setSpanBusy(true);
    setSpanError("");
    try {
      setCovering(await getOrgSettlementCovering(tenantId, spanFrom, spanTo));
    } catch (err) {
      console.error("Failed covering lookup:", err);
      setSpanError("Couldn't load the covering quarters.");
    } finally {
      setSpanBusy(false);
    }
  };

  // --- guard: no tenant ------------------------------------------------------
  if (!tenantId) {
    return (
      <Shell>
        <h1 style={styles.h1}>My Settlements</h1>
        <div style={styles.state}>
          <div style={styles.errorBox}>
            <AlertCircle size={18} />
            <span>No organisation selected. Please sign in to an organisation account to view its settlements.</span>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={styles.h1}>My Settlements</h1>
      <p style={styles.sub}>Your organisation's settled quarters (calendar quarters, Jan start).</p>

      {loading && <div style={styles.state}>Loading your settlements…</div>}

      {!loading && error && (
        <div style={styles.state}>
          <div style={styles.errorBox}>
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ---- KPIs (latest settled quarter) ---- */}
          <div style={styles.kpiRow}>
            <div style={styles.card}>
              <div style={styles.kpiLabel}><IndianRupee size={14} /> My total (latest settled)</div>
              <div style={styles.kpiValue}>{inr(summary?.total ?? 0)}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.kpiLabel}><Zap size={14} /> Energy delivered</div>
              <div style={styles.kpiValue}>{kwh(summary?.kwh ?? 0)}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.kpiLabel}><CalendarClock size={14} /> Period</div>
              <div style={styles.kpiValue}>{summary?.period_key ? fmtPeriodKey(summary.period_key) : "—"}</div>
            </div>
          </div>

          {/* ---- Per-station (latest settled quarter) ---- */}
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>My stations — {summary?.period_key ? fmtPeriodKey(summary.period_key) : "latest period"}</h2>
            <div style={styles.whereItGoes}>
              Where it goes — at each station you receive your electricity reimbursement plus your agreed per-kWh
              share. Plain and transparent: energy delivered × the agreed figures.
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Station</th>
                    <th style={{ ...styles.th, ...styles.thR }}>Energy</th>
                    <th style={{ ...styles.th, ...styles.thR }}>Electricity reimbursed</th>
                    <th style={{ ...styles.th, ...styles.thR }}>My share</th>
                    <th style={{ ...styles.th, ...styles.thR }}>Total to me</th>
                  </tr>
                </thead>
                <tbody>
                  {summary && summary.stations.length > 0 ? (
                    summary.stations.map((s) => (
                      <tr key={s.station_id}>
                        <td style={styles.td}>{s.station_name}</td>
                        <td style={{ ...styles.td, ...styles.tdR }}>{kwh(s.kwh)}</td>
                        <td style={{ ...styles.td, ...styles.tdR }}>{inr(s.reimbursement)}</td>
                        <td style={{ ...styles.td, ...styles.tdR }}>{inr(s.share)}</td>
                        <td style={{ ...styles.td, ...styles.tdR, ...styles.tdStrong }}>{inr(s.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td style={{ ...styles.td, color: "#9ca3af" }} colSpan={5}>
                        No settled station activity yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Statements ---- */}
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>Statements</h2>
            <p style={styles.sub}>Your settled quarters.</p>
            <StatementsTable rows={statements} empty="No settled quarters yet." />
          </div>

          {/* ---- Look up a period → covering quarters ---- */}
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>Look up a period</h2>
            <p style={styles.sub}>
              Pick any date range — we'll show the settled quarters that cover it (e.g. Feb–May returns Q1 and Q2).
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "center", marginBottom: "1rem" }}>
              <input type="date" value={spanFrom} onChange={(e) => setSpanFrom(e.target.value)} style={styles.dateInput} aria-label="From date" />
              <span style={{ color: "#6b7280" }}>to</span>
              <input type="date" value={spanTo} onChange={(e) => setSpanTo(e.target.value)} style={styles.dateInput} aria-label="To date" />
              <button style={{ ...styles.btn, opacity: spanBusy ? 0.6 : 1 }} onClick={runSpan} disabled={spanBusy}>
                <Search size={15} /> {spanBusy ? "Loading…" : "Show covering quarters"}
              </button>
              {spanError && <span style={{ color: "#991b1b", fontSize: "0.8rem" }}>{spanError}</span>}
            </div>
            {covering !== null && (
              <StatementsTable rows={covering} empty="No settled quarters cover that range." />
            )}
          </div>
        </>
      )}
    </Shell>
  );
};

export default OrgSettlements;
